import { ColumnDef } from "@tanstack/react-table";
import { JourneyTableData, JourneySortField } from "./types";
import {
  EMPTY_CELL,
  formatCount,
  formatRatePercent,
  getCompletionBarWidth,
} from "./functions";
import SortByIcon from "../../../../../../assets/svg/sort-by-icon";
import { TruncatedTextTooltip } from "../../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { getDateTimeByFormat } from "../../../../../../redux/slices/dateTimeFormatSlice";
import {
  getChannelIcon,
  getModalityDisplayName,
} from "../../../../../Campaigns/utils/functions";
import { Tooltip } from "../../../../../../components/Tooltip/Tooltip";
import JourneyStatusPill from "../components/StatusPill";
import { Avatar } from "../../../../../../components/Avatar/Avatar";
import DocumentTextIcon from "../../../../../../assets/svg/document-text-icon";
import PlayCircleIcon from "../../../../../../assets/svg/play-circle-icon";
import StopCircleIcon from "../../../../../../assets/svg/stop-circle-icon";
import TrashIcon from "../../../../../../assets/svg/trash-icon";
// import ArchiveBookIcon from "../../../../../../../assets/svg/archive-book-icon";
// import TimerIcon from "../../../../../../../assets/svg/timer-icon";

export enum JOURNEY_STATUS {
  DRAFT = "draft",
  DELETED = "deleted",
  PAUSED = "paused",
  ACTIVE = "active",
  INACTIVE = "inactive",
  // STOPPED = "stopped",
  // ARCHIVED = "archived",
  // IDLE = "idle",
}

export const JOURNEY_STATUS_OPTIONS = [
  { value: "all", content: "All" },
  { value: JOURNEY_STATUS.DRAFT, content: "Draft" },
  { value: JOURNEY_STATUS.ACTIVE, content: "Active" },
  { value: JOURNEY_STATUS.PAUSED, content: "Paused" },
  // { value: JOURNEY_STATUS.DELETED, content: "Deleted" },
  { value: JOURNEY_STATUS.INACTIVE, content: "Inactive" },
  // { value: JOURNEY_STATUS.STOPPED, content: "Stopped" },
  // { value: JOURNEY_STATUS.ARCHIVED, content: "Archived" },
  // { value: JOURNEY_STATUS.IDLE, content: "Idle" },
];

export const JOURNEY_STATUS_COLOR_CONFIG: Record<
  JOURNEY_STATUS,
  {
    bgColor: string;
    className: string;
    Icon: React.ElementType;
  }
> = {
  [JOURNEY_STATUS.DRAFT]: {
    bgColor: "bg-tags-gray-bg dark:bg-tags-gray-bg-dk",
    className:
      "border-journey-status-draft dark:border-journey-status-draft-dk text-journey-status-draft dark:text-journey-status-draft-dk",
    Icon: DocumentTextIcon,
  },
  [JOURNEY_STATUS.ACTIVE]: {
    bgColor: "bg-tags-green-bg dark:bg-tags-green-bg-dk",
    className:
      "border-journey-status-active dark:border-journey-status-active-dk text-journey-status-active dark:text-journey-status-active-dk",
    Icon: PlayCircleIcon,
  },
  [JOURNEY_STATUS.PAUSED]: {
    bgColor: "bg-tags-red-bg dark:bg-tags-red-bg-dk",
    className:
      "border-journey-status-stopped dark:border-journey-status-stopped-dk text-journey-status-stopped dark:text-journey-status-stopped-dk",
    Icon: StopCircleIcon,
  },
  [JOURNEY_STATUS.DELETED]: {
    bgColor:
      "bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
    className:
      "border-journey-status-deleted dark:border-journey-status-deleted-dk text-journey-status-deleted dark:text-journey-status-deleted-dk",
    Icon: TrashIcon,
  },
  [JOURNEY_STATUS.INACTIVE]: {
    bgColor: "bg-tags-red-bg dark:bg-tags-red-bg-dk",
    className:
      "border-journey-status-stopped dark:border-journey-status-stopped-dk text-journey-status-stopped dark:text-journey-status-stopped-dk",
    Icon: StopCircleIcon,
  },

  // archive: {
  //   bgColor: "bg-lavender-bg dark:bg-lavender-bg-dk",
  //   className:
  //     "border-journey-status-archived dark:border-journey-status-archived-dk text-journey-status-archived dark:text-journey-status-archived-dk  ",
  //   Icon: ArchiveBookIcon,
  // },
  // idle: {
  //   bgColor: "bg-tags-orange-bg dark:bg-tags-orange-bg-dk",
  //   className:
  //     "border-journey-status-idle dark:border-journey-status-idle-dk text-journey-status-idle dark:text-journey-status-idle-dk",
  //   Icon: TimerIcon,
  // },
};

/** Non-sortable column header — the five run-count columns all share this markup. */
const renderColumnHeader = (title: string) => (
  <div className="px-3 py-3">
    <p className="text-md text-left font-medium text-gray-375 dark:text-gray-375-dk">
      {title}
    </p>
  </div>
);

/** Plain count cell for Running / Completed / Failed. */
const renderCountCell = (count?: number) => (
  <span className="text-md text-heading-text dark:text-heading-text-dk">
    {formatCount(count)}
  </span>
);

export const createJourneyColumns = (
  onNameClick: (row: JourneyTableData) => void,
  sortField: JourneySortField,
  sortOrder: "asc" | "desc",
  onSort: (field: JourneySortField) => void,
): ColumnDef<JourneyTableData>[] => [
  {
    accessorKey: "journeyName",
    minSize: 180,
    header: () => (
      <div
        className="flex items-center gap-1 px-3 py-3 cursor-pointer select-none"
        onClick={() => onSort("title")}
      >
        <p className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Journey Name
        </p>
        <SortByIcon
          className={`w-[18px] h-[18px] transition-all ease-in-out duration-300 ${
            sortField === "title"
              ? `text-verba-blue dark:text-verba-blue-dk ${sortOrder === "desc" ? "rotate-180" : ""}`
              : "text-gray-375 dark:text-gray-375-dk"
          }`}
        />
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex flex-col gap-1 py-1">
        <TruncatedTextTooltip
          text={row.original.journeyName}
          onClick={() => onNameClick(row.original)}
          maxWidth="max-w-[250px]"
          className="text-md font-medium underline hover:underline-offset-1 text-heading-text dark:text-heading-text-dk hover:text-verba-blue dark:hover:text-verba-blue-dk"
        >
          <span>{row.original.journeyName}</span>
        </TruncatedTextTooltip>
        {/* Guarded like `modalities` below — the mapper defaults both to `[]`, but the cell has no
            way to know that and a future producer needn't. */}
        {(row.original?.tags?.length ?? 0) > 0 && (
          <div className="max-w-[250px] flex items-center gap-1 flex-wrap">
            {(row.original?.tags ?? []).map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="text-sm px-2 py-0.5 rounded-full bg-user-management-blue-card-bg dark:bg-user-management-blue-card-bg-dk text-verba-blue dark:text-verba-blue-dk"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    ),
  },
  {
    accessorKey: "status",
    size: 130,
    header: () => (
      <div className="px-3 py-3">
        <p className="text-md text-left font-medium text-gray-375 dark:text-gray-375-dk">
          Status
        </p>
      </div>
    ),
    cell: ({ row }) => (
      <div className="">
        <JourneyStatusPill status={row.original.status} />
      </div>
    ),
  },
  {
    accessorKey: "modalities",
    size: 120,
    header: () => renderColumnHeader("Modalities Used"),
    cell: ({ row }) => (
      <div className="flex items-center gap-0.5 ">
        {(row.original.modalities ?? []).map((modality, idx) => (
          <Tooltip key={`${modality}-${idx}`} content={getModalityDisplayName(modality)}>
            <div className="flex items-center justify-center rounded-full w-6 h-6 border border-gray-50 dark:border-gray-50-dk">
              {getChannelIcon(modality)}
            </div>
          </Tooltip>
        ))}
      </div>
    ),
  },
  {
    accessorKey: "runningCount",
    size: 85,
    header: () => renderColumnHeader("Running"),
    cell: ({ row }) => renderCountCell(row.original.runningCount),
  },
  {
    accessorKey: "completedCount",
    size: 85,
    header: () => renderColumnHeader("Completed"),
    cell: ({ row }) => renderCountCell(row.original.completedCount),
  },
  {
    accessorKey: "failedCount",
    size: 85,
    header: () => renderColumnHeader("Failed"),
    cell: ({ row }) => renderCountCell(row.original.failedCount),
  },
  {
    accessorKey: "completionRate",
    size: 130,
    header: () => renderColumnHeader("Completion"),
    cell: ({ row }) => {
      const { completionRate, status, journeyName } = row.original;
      if (typeof completionRate !== "number") {
        return (
          <span className="text-md text-heading-text dark:text-heading-text-dk">
            {EMPTY_CELL}
          </span>
        );
      }
      const barWidth = getCompletionBarWidth(completionRate);
      // Green tracks the Active status (same green as its pill), blue for every other status.
      const fillColor =
        status === JOURNEY_STATUS.ACTIVE
          ? "bg-verba-green-success dark:bg-verba-green-success-dk"
          : "bg-verba-blue dark:bg-verba-blue-dk";
      return (
        <div className="flex items-center gap-2">
          <div
            role="progressbar"
            aria-label={`${journeyName} completion rate`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={barWidth}
            className="relative h-1.5 w-14 shrink-0 rounded-[3px] bg-gray-375/10 dark:bg-gray-375-dk/15"
          >
            {barWidth > 0 && (
              <div
                className={`absolute inset-y-0 left-0 rounded-[3px] ${fillColor}`}
                style={{ width: `${barWidth}%` }}
              />
            )}
          </div>
          <span className="text-md font-medium whitespace-nowrap text-verba-indigo dark:text-verba-indigo-dk">
            {formatRatePercent(completionRate)}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "errorRate",
    size: 90,
    header: () => renderColumnHeader("Error rate"),
    cell: ({ row }) => {
      const { errorRate } = row.original;
      // Any failure at all is called out in red; a clean 0% stays in the default text colour.
      const hasErrors = typeof errorRate === "number" && errorRate > 0;
      return (
        <span
          className={`text-md ${
            hasErrors
              ? "font-semibold text-verba-red-alert dark:text-verba-red-alert-dk"
              : "text-verba-indigo dark:text-verba-indigo-dk"
          }`}
        >
          {formatRatePercent(errorRate)}
        </span>
      );
    },
  },
  {
    accessorKey: "description",
    minSize: 150,
    header: () => renderColumnHeader("Description"),
    cell: ({ row }) => (
      <div className="">
        <TruncatedTextTooltip
          text={row.original.description || EMPTY_CELL}
          maxWidth="max-w-[240px]"
          className="text-md text-heading-text dark:text-heading-text-dk"
        >
          <p className="text-md text-heading-text dark:text-heading-text-dk whitespace-pre-wrap line-clamp-2">
            {row.original.description || EMPTY_CELL}
          </p>
        </TruncatedTextTooltip>
      </div>
    ),
  },
  {
    accessorKey: "lastEditedBy",
    size: 160,
    header: () => (
      <div className="px-3 py-3">
        <p className="text-md text-left font-medium text-gray-375 dark:text-gray-375-dk">
          Last Edited By
        </p>
      </div>
    ),
    cell: ({ row }) => (
      <>
        {row.original.lastEditedBy && (
          <div className="flex flex-col items-start gap-0.5">
            <div className="flex items-center justify-start gap-1">
              <Avatar
                text={row.original.lastEditedBy}
                size={18}
                className="p-[2px]! shrink-0"
              />
              <span className="text-xs font-medium text-heading-text dark:text-heading-text-dk">
                {row.original.lastEditedBy}
              </span>
            </div>
            <div className="">
              {row.original.lastEditedByDate && (
                <span className="text-xs text-data dark:text-data-dk">
                  {(() => {
                    const { date, time } = getDateTimeByFormat(
                      new Date(row.original.lastEditedByDate),
                    );
                    return `${date}, ${time}`;
                  })()}
                </span>
              )}
            </div>
          </div>
        )}
      </>
    ),
  },
  {
    accessorKey: "lastEdited",
    size: 160,
    header: () => (
      <div
        className="flex items-center gap-1 px-3 py-3 cursor-pointer select-none"
        onClick={() => onSort("updatedAt")}
      >
        <p className="text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Last Edited
        </p>
        <SortByIcon
          className={`w-[18px] h-[18px] transition-transform duration-200 ${
            sortField === "updatedAt"
              ? `text-verba-blue dark:text-verba-blue-dk ${sortOrder === "desc" ? "rotate-180" : ""}`
              : "text-gray-375 dark:text-gray-375-dk"
          }`}
        />
      </div>
    ),
    cell: ({ row }) => {
      const { date, time } = getDateTimeByFormat(
        new Date(row.original.lastEdited),
      );
      return (
        <div className="flex flex-col">
          <span className="text-md text-heading-text dark:text-heading-text-dk">
            {date}
          </span>
          <span className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
            {time}
            {" (Local time)"}
          </span>
        </div>
      );
    },
  },
];
